import { z } from 'zod';
import { PrintProtocolSchema, PrinterIdSchema, TenantIdSchema } from './schemas';

/**
 * Contratos das operações de impressora feitas FORA do aparelho — tela do
 * cliente do CRM (#776): cadastrar por MAC, editar nome/protocolo/observação,
 * padrão, desativar/reativar, remover e pedir uma etiqueta de teste.
 *
 * Origem: #776 — 2026-09-29 12:55 +0200 (owner: "impressoras deve poder
 * adicionar/remover/editar e até mandar imprimir teste se estiver conectada").
 *
 * Por quê: até aqui só o Android escrevia `tenants/{tid}/printers` (direto,
 * pelas rules de manager), porque a impressora nasce do pareamento Bluetooth.
 * O CRM não é membro do tenant — as rules o recusam —, então as escritas dele
 * passam por callables com estes schemas. Os campos de CALIBRAÇÃO (dpi, gap,
 * densidade, velocidade, margens) não entram: o Android os fixa
 * (`PrinterProfile.FIXED_*`) e o wizard os mede; editar pela web os
 * dessincronizaria do aparelho.
 *
 * Usado por: callables `upsertPrinter`, `setPrinterActive`, `deletePrinter`,
 * `setDefaultPrinter`, `requestTestPrint`, `getPrintRequest`
 * (`apps/functions/src/printers/`); CRM `PrintersTab`/`PrinterEditorPage`;
 * Android lê `PrintRequest` (`tenants/{tid}/printers/{pid}/printRequests`).
 *
 * Issues: #776.
 */

/** MAC como o Android grava (`AA:BB:CC:DD:EE:FF`). Aceita `-` e minúsculas na entrada. */
export const PrinterMacInputSchema = z
  .string()
  .trim()
  .transform((v) => v.toUpperCase().replace(/-/g, ':'))
  .pipe(z.string().regex(/^([0-9A-F]{2}:){5}[0-9A-F]{2}$/, { message: 'MAC inválido — use AA:BB:CC:DD:EE:FF.' }));

/**
 * Id do documento a partir do MAC — espelho de `printerDocId` do Android
 * (`FirestorePrinterRepository.kt`: maiúsculas, `:` → `-`). O mesmo MAC cai
 * SEMPRE no mesmo doc: cadastrar pelo CRM uma impressora que o aparelho parear
 * depois reusa o perfil em vez de duplicar (o Android faz create-or-open).
 */
export function printerDocIdFromMac(mac: string): string {
  return mac.toUpperCase().replace(/:/g, '-');
}

/**
 * Campos editáveis pelo CRM — nada de calibração (ver cabeçalho). O protocolo
 * também não: o Android o fixa em TSPL (`PrinterProfile.protocol`), então
 * trocá-lo pela web não mudaria nada no papel e só enganaria quem lê.
 */
const PrinterEditableFields = {
  name: z.string().trim().min(1).max(60),
  notes: z.string().max(80).optional(),
};

export const UpsertPrinterInput = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('create'),
    tenantId: TenantIdSchema,
    macAddress: PrinterMacInputSchema,
    model: z.string().trim().max(120).optional(),
    /** Gravado no cadastro para o iOS/web lerem; o Android usa TSPL sempre. */
    protocol: PrintProtocolSchema.default('TSPL'),
    ...PrinterEditableFields,
  }),
  z.object({
    mode: z.literal('update'),
    tenantId: TenantIdSchema,
    printerId: PrinterIdSchema,
    ...PrinterEditableFields,
  }),
]);
export type UpsertPrinterInput = z.input<typeof UpsertPrinterInput>;
export const UpsertPrinterOutput = z.object({
  printerId: z.string(),
  /** `true` = já havia perfil para o MAC (foi reativado/atualizado, não duplicado). */
  reused: z.boolean(),
});
export type UpsertPrinterOutput = z.infer<typeof UpsertPrinterOutput>;

export const PrinterRefInput = z.object({ tenantId: TenantIdSchema, printerId: PrinterIdSchema });
export type PrinterRefInput = z.input<typeof PrinterRefInput>;
export const SetPrinterActiveInput = PrinterRefInput.extend({ active: z.boolean() });
export type SetPrinterActiveInput = z.input<typeof SetPrinterActiveInput>;

/** Estados de um pedido de impressão remota. */
export const PrintRequestStatus = {
  /** Gravado pelo servidor; o aparelho ainda não respondeu. */
  PENDING: 'PENDING',
  /** O aparelho conectado a esta impressora mandou a etiqueta. */
  DONE: 'DONE',
  /** Algum aparelho recebeu, mas nenhum está conectado a esta impressora. */
  NOT_CONNECTED: 'NOT_CONNECTED',
  /** Estava conectado e a escrita falhou (`error` diz o motivo). */
  FAILED: 'FAILED',
} as const;
export type PrintRequestStatus = (typeof PrintRequestStatus)[keyof typeof PrintRequestStatus];
export const ALL_PRINT_REQUEST_STATUSES = Object.values(PrintRequestStatus) as [PrintRequestStatus, ...PrintRequestStatus[]];

/**
 * Pedido de etiqueta de teste — `tenants/{tid}/printers/{pid}/printRequests/{id}`.
 *
 * Dados (exemplo):
 *   { "id": "req-1", "tenantId": "tenant-1", "printerId": "AA-BB-CC-DD-EE-FF",
 *     "kind": "TEST", "status": "PENDING", "requestedByUid": "uid-1",
 *     "requestedByEmail": "vendedor@…", "createdAt": 1727600000000,
 *     "expiresAt": <Timestamp +1 dia>, "handledAt": null, "handledByDevice": null, "error": null }
 *
 * Quem escreve: o servidor cria (`requestTestPrint`); o Android atualiza só
 * `status`/`handledAt`/`handledByDevice`/`error` (rules). `NOT_CONNECTED` é
 * gravado só por aparelho que NÃO segura a impressora e só se ainda estiver
 * `PENDING` — o que a imprimiu vence (`DONE` nunca é sobrescrito). `expiresAt`
 * e `handledAt` são Timestamp (TTL do Firestore / `serverTimestamp` do
 * aparelho); `createdAt` é millis.
 */
export const PrintRequestSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  printerId: z.string(),
  kind: z.literal('TEST'),
  status: z.enum(ALL_PRINT_REQUEST_STATUSES).catch(PrintRequestStatus.PENDING),
  requestedByUid: z.string(),
  requestedByEmail: z.string().nullable().optional(),
  createdAt: z.number(),
  /**
   * `serverTimestamp()` gravado pelo aparelho — chega como Timestamp do Admin
   * SDK, não como número. Ninguém decide nada por ele (só diagnóstico), então
   * não é validado: tipá-lo como número fazia o `getPrintRequest` recusar
   * justamente o pedido JÁ RESPONDIDO (#776, achado na revisão).
   */
  handledAt: z.unknown().optional(),
  handledByDevice: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
});
export type PrintRequest = z.infer<typeof PrintRequestSchema>;

export const RequestTestPrintOutput = z.object({
  requestId: z.string(),
  /** Aparelhos Android do tenant que receberam o aviso (0 = ninguém vai imprimir). */
  devices: z.number().int().nonnegative(),
});
export type RequestTestPrintOutput = z.infer<typeof RequestTestPrintOutput>;

export const GetPrintRequestInput = PrinterRefInput.extend({ requestId: z.string().min(1) });
export type GetPrintRequestInput = z.input<typeof GetPrintRequestInput>;
export const GetPrintRequestOutput = z.object({
  status: z.enum(ALL_PRINT_REQUEST_STATUSES),
  error: z.string().nullable(),
});
export type GetPrintRequestOutput = z.infer<typeof GetPrintRequestOutput>;
